import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, Copy, Ban, ChevronLeft } from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '../services/apiClient';
import { Button, Badge, ConfirmDialog, Modal, Input, SelectField, PageHeader, EmptyState, Spinner } from '../components/ui';

// ─── Types ────────────────────────────────────────────────────────────────────

interface InviteCode {
  id: string;
  event_id: string;
  code: string;
  ticket_type: string;
  assigns_role: string;
  max_uses: number | null;
  uses_count: number;
  expires_at: string | null;
  revoked_at: string | null;
  created_at: string;
}

// ─── Form schema ──────────────────────────────────────────────────────────────

const codeSchema = z.object({
  code:         z.string().min(3, 'Code must be at least 3 characters').max(64),
  ticket_type:  z.string().min(1),
  assigns_role: z.enum(['attendee', 'speaker', 'vendor']),
  max_uses:     z.coerce.number().min(1).optional(),
  expires_at:   z.string().optional(),
});

type CodeForm = z.infer<typeof codeSchema>;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function statusBadge(code: InviteCode) {
  if (code.revoked_at) return <Badge variant="danger">Revoked</Badge>;
  if (code.expires_at && new Date(code.expires_at) <= new Date())
    return <Badge variant="default">Expired</Badge>;
  if (code.max_uses !== null && code.uses_count >= code.max_uses)
    return <Badge variant="default">Exhausted</Badge>;
  return <Badge variant="success">Active</Badge>;
}

// ─── Create modal ─────────────────────────────────────────────────────────────

function CreateModal({ open, onClose, eventId, onSaved }: {
  open: boolean; onClose: () => void; eventId: string; onSaved: () => void;
}) {
  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm<CodeForm>({
    resolver: zodResolver(codeSchema),
    defaultValues: { code: '', ticket_type: 'general', assigns_role: 'attendee' },
  });

  useEffect(() => { if (open) reset({ code: '', ticket_type: 'general', assigns_role: 'attendee' }); }, [open, reset]);

  const onSubmit = async (data: CodeForm) => {
    try {
      await apiClient.post(`/admin/events/${eventId}/invite-codes`, {
        ...data,
        max_uses: data.max_uses || null,
        expires_at: data.expires_at || null,
      });
      toast.success('Invite code created');
      onSaved();
      onClose();
    } catch (err: unknown) {
      const detail = (err as { detail?: { reason?: string; message?: string } })?.detail;
      if (detail?.reason === 'code_taken') {
        toast.error('Code already in use', { description: 'Choose a different code.' });
      } else {
        toast.error('Failed to create code', { description: detail?.message ?? 'Unknown error' });
      }
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Create Invite Code" size="md">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Input
          label="Code *"
          placeholder="NEURIPS26-STUDENT"
          error={errors.code?.message}
          {...register('code')}
        />
        <div className="grid grid-cols-2 gap-3">
          <Input label="Ticket Type" placeholder="general" {...register('ticket_type')} />
          <SelectField
            label="Assigns Role *"
            options={[
              { value: 'attendee', label: 'Attendee' },
              { value: 'speaker',  label: 'Speaker'  },
              { value: 'vendor',   label: 'Vendor'   },
            ]}
            {...register('assigns_role')}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input label="Max Uses" type="number" placeholder="1000 (default)" {...register('max_uses')} />
          <Input label="Expires At" type="datetime-local" {...register('expires_at')} />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={isSubmitting}>Create Code</Button>
        </div>
      </form>
    </Modal>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function InviteCodesPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const [codes, setCodes]       = useState<InviteCode[]>([]);
  const [loading, setLoading]   = useState(true);
  const [modalOpen, setModal]   = useState(false);
  const [revoking, setRevoking] = useState<string | null>(null);
  const [codeToRevoke, setCodeToRevoke] = useState<InviteCode | null>(null);

  async function load() {
    setLoading(true);
    try {
      const data = await apiClient.get<InviteCode[]>(`/admin/events/${eventId}/invite-codes`);
      setCodes(data);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [eventId]);

  async function revoke(id: string) {
    setRevoking(id);
    try {
      await apiClient.patch(`/admin/events/${eventId}/invite-codes/${id}`, { revoke: true });
      toast.success('Code revoked');
      setCodeToRevoke(null);
      load();
    } catch {
      toast.error('Failed to revoke code');
    } finally {
      setRevoking(null);
    }
  }

  function copyCode(code: string) {
    navigator.clipboard.writeText(code).then(() => toast.success('Copied to clipboard'));
  }

  return (
    <div className="p-8">
      <div className="mb-4">
        <Link to="/events" className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-gray-200 transition-colors">
          <ChevronLeft size={12} /> Back to Events
        </Link>
      </div>
      <PageHeader
        title="Invite Codes"
        description="Manage invite codes for this event. Codes are globally unique across all events."
        action={<Button icon={<Plus size={14} />} onClick={() => setModal(true)}>New Code</Button>}
      />

      {loading ? (
        <div className="flex justify-center py-20"><Spinner className="w-6 h-6 text-gray-500" /></div>
      ) : codes.length === 0 ? (
        <EmptyState
          icon={Plus}
          title="No invite codes yet"
          description="Create your first code to let attendees join this event."
          action={<Button icon={<Plus size={14} />} onClick={() => setModal(true)}>Create Code</Button>}
        />
      ) : (
        <div className="overflow-hidden border border-gray-800 rounded-xl">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800 bg-gray-900">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400">Code</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400">Ticket / Role</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400">Uses</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400">Expires</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {codes.map((c) => (
                <tr key={c.id} className="bg-gray-900/50 hover:bg-gray-800/40 transition-colors">
                  <td className="px-4 py-3 font-mono text-white">{c.code}</td>
                  <td className="px-4 py-3 text-gray-300">
                    {c.ticket_type} / {c.assigns_role}
                  </td>
                  <td className="px-4 py-3 text-gray-300">
                    {c.uses_count}{c.max_uses !== null ? ` / ${c.max_uses}` : ''}
                  </td>
                  <td className="px-4 py-3 text-gray-400 text-xs">
                    {c.expires_at ? new Date(c.expires_at).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-4 py-3">{statusBadge(c)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 justify-end">
                      <Button
                        variant="ghost" size="sm"
                        icon={<Copy size={12} />}
                        onClick={() => copyCode(c.code)}
                        title="Copy code"
                      />
                      {!c.revoked_at && (
                        <Button
                          variant="ghost" size="sm"
                          icon={<Ban size={12} />}
                          loading={revoking === c.id}
                          onClick={() => setCodeToRevoke(c)}
                          title="Revoke code"
                          className="text-red-400 hover:text-red-300"
                        />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CreateModal
        open={modalOpen}
        onClose={() => setModal(false)}
        eventId={eventId!}
        onSaved={load}
      />
      <ConfirmDialog open={codeToRevoke !== null} onClose={() => setCodeToRevoke(null)} onConfirm={() => codeToRevoke && void revoke(codeToRevoke.id)} loading={revoking !== null}
        title="Revoke invite code?" description={`Revoke ${codeToRevoke?.code ?? 'this code'}? Attendees will no longer be able to use it.`} confirmLabel="Revoke code" />
    </div>
  );
}
